import * as XLSX from "xlsx"

const itemHeader =
  /^(?:(?:phoenix|manufacturer)\s+)?(?:item|article|part|ordering)\s+(?:no\.?|numbers?|code)$|^mpn$/i
export type ArticleImportErrorCode =
  | "empty"
  | "column"
  | "limit"
  | "worksheet"
  | "table"
  | "size"
export class ArticleImportError extends Error {
  constructor(readonly code: ArticleImportErrorCode) {
    super(code)
  }
}
export type ArticleImportRow = {
  itemNumber: string
  masterData: string
  description: string
}

function fromRows(rows: string[][]): ArticleImportRow[] {
  const nonempty = rows.filter((row) => row.some((cell) => cell.trim()))
  if (!nonempty.length) throw new ArticleImportError("empty")
  const header = nonempty[0]!
  const column = header.findIndex((cell) => itemHeader.test(cell.trim()))
  const multiColumn = nonempty.some((row) => row.length > 1)
  if (multiColumn && column < 0) throw new ArticleImportError("column")
  const index = column < 0 ? 0 : column
  const masterIndex = header.findIndex((cell) =>
    /(?:hik|customer).*master data/i.test(cell),
  )
  const descriptionIndex = header.findIndex((cell) => /description/i.test(cell))
  const data = column >= 0 ? nonempty.slice(1) : nonempty
  const unique = new Map<string, ArticleImportRow>()
  for (const row of data) {
    const itemNumber = (row[index] || "").trim()
    if (itemNumber && !unique.has(itemNumber))
      unique.set(itemNumber, {
        itemNumber,
        masterData: masterIndex >= 0 ? (row[masterIndex] || "").trim() : "",
        description:
          descriptionIndex >= 0 ? (row[descriptionIndex] || "").trim() : "",
      })
  }
  const result = [...unique.values()]
  if (
    !result.length ||
    result.length > 1000 ||
    result.some(
      (item) =>
        item.itemNumber.length > 200 ||
        [...item.itemNumber].some(
          (char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127,
        ),
    )
  )
    throw new ArticleImportError("limit")
  return result
}

export function parseArticlePaste(value: string): string[] {
  return parseArticlePasteRows(value).map((row) => row.itemNumber)
}

export function parseArticlePasteRows(value: string): ArticleImportRow[] {
  return fromRows(parseTsv(value))
}

function parseTsv(value: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ""
  let quoted = false
  for (let i = 0; i < value.length; i++) {
    const char = value[i]
    if (char === '"') {
      if (quoted && value[i + 1] === '"') {
        cell += '"'
        i++
      } else if (quoted) {
        quoted = false
      } else if (!cell) {
        quoted = true
      } else {
        cell += char
      }
    } else if (!quoted && (char === "\t" || char === "\n" || char === "\r")) {
      row.push(cell)
      cell = ""
      if (char !== "\t") {
        rows.push(row)
        row = []
        if (char === "\r" && value[i + 1] === "\n") i++
      }
    } else {
      cell += char
    }
  }
  if (quoted) throw new ArticleImportError("table")
  row.push(cell)
  rows.push(row)
  return rows
}

export function parseArticleWorkbook(bytes: ArrayBuffer): string[] {
  return parseArticleWorkbookRows(bytes).map((row) => row.itemNumber)
}

export function parseArticleWorkbookRows(
  bytes: ArrayBuffer,
): ArticleImportRow[] {
  const book = XLSX.read(bytes, { type: "array" })
  const sheet = book.Sheets[book.SheetNames[0] || ""]
  if (!sheet) throw new ArticleImportError("worksheet")
  const rows = XLSX.utils.sheet_to_json<string[]>(sheet, {
    header: 1,
    raw: false,
    defval: "",
  })
  return fromRows(rows.map((row) => row.map(String)))
}
